import moment from 'moment';
import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { DecimalPipe } from "@angular/common";
import { Side } from "../types/trading.types";
import { interval as observableInterval } from "rxjs";
import { takeWhile, scan, tap } from "rxjs/operators";
import { TimeZone } from '../types/users.types';

const _decimalPipe = new DecimalPipe('en-US');

export type BuilderReturn = (firstKey: string, secondKey: string) => {};
export type HasErrorBuilder = (formGroup: FormGroup) => BuilderReturn;
/**
 * @description
 * checker for errors on a form based on DIRTY, TOUCHED or VALID.
 * @param FormGroup that will be used as the form to get the different entries provided
 * @returns Anonymous function with the shape of (key: string)=> boolean, this will work as
 * an ascesor for the different keys of the form.
 */
export const hasErrorBuilder: HasErrorBuilder =
  (formGroup: FormGroup) =>
  (firstKey: string, secondKey: string): boolean => {
    if (!firstKey) {
      return false;
    }

    let entry = formGroup.get(firstKey);
    if (secondKey && entry) {
      entry = entry.get(secondKey);
    }

    if (!entry) {
      return false;
    }

    const entryError = !entry.valid && (entry.dirty || entry.touched);

    const formErrors = formGroup.errors || {};

    const result = entryError || Object.keys(formErrors[firstKey] || {}).length;
    return !!result;
  };

export const sortStringArray = (array: string[], sortDirection: string = 'asc'): string[] => {
  if (sortDirection === 'asc') {
    return array.sort((a, b) => {
      return a.toLowerCase().localeCompare(b.toLowerCase());
    });
  } else if (sortDirection === 'desc') {
    return array.sort((a, b) => {
      return b.toLowerCase().localeCompare(a.toLowerCase());
    });
  } else {
    return [];
  }
};

export const dateFormat = (timestamp: any, defaultValue?: string, timezone?: string) => {
  // return this in case the date was not set
  if (!timestamp) return defaultValue || '-';
  // If timestamp is set in seconds (10 digits) we need to use unix formatter
  if (typeof timestamp === 'number' && timestamp.toString().length === 10) {
    return timezone && timezone === TimeZone.UTC ? moment.unix(timestamp).utc().format('MM/DD/YY') : moment.unix(timestamp).format('MM/DD/YY');
  } else {
    return timezone && timezone === TimeZone.UTC ? moment(timestamp).utc().format('MM/DD/YY') : moment(timestamp).format('MM/DD/YY');
  }
};

export const timeFormat = (timestamp: number, justTime?: boolean, timezone?: string, showMilliseconds?: boolean) => {
  // return this in case the date was not set
  if (!timestamp) return '-';
  // If timestamp is set in seconds (10 digits) we need to use unix formatter
  const format = justTime ? 'HH:mm:ss' : showMilliseconds ? 'MM/DD/YY HH:mm:ss.SSS' : 'MM/DD/YY HH:mm:ss';
  if (timestamp.toString().length === 10) {
    return timezone && timezone === TimeZone.UTC ? moment.unix(timestamp).utc().format(format) : moment.unix(timestamp).format(format);
  } else {
    if (timestamp.toString().length === 19) {
      timestamp = timestamp / 1000000;
    }
    return timezone && timezone === TimeZone.UTC ? moment(timestamp).utc().format(format) : moment(timestamp).format(format);
  }
};

export const parseDateFromString = (date: string) => {
  return new Date(date.includes('Z') ? date : date + 'Z');
};

export const copyToClipboard = (text: string) => {
  // temporarily creating an element to place the url, and select to copy to clipboard
  // then remove the element
  const selBox = document.createElement('textarea');
  selBox.style.position = 'fixed';
  selBox.style.left = '0';
  selBox.style.top = '0';
  selBox.style.opacity = '0';
  selBox.value = text;
  document.body.appendChild(selBox);
  selBox.focus();
  selBox.select();
  if (text) {
    document.execCommand('copy');
  }
  document.body.removeChild(selBox);

  return 1;
};

export const _priceVariableDecimalsRenderer = (value: any, minDecimals: number, maxDecimals: number) => {
  const formattedValue = _decimalPipe.transform(Math.abs(value), `1.${minDecimals}-${maxDecimals}`);
  return value < 0 ? '(' + formattedValue + ')' : formattedValue;
};

export const _priceRenderer = (value: any, decimals: number) => {
  const formattedValue = _decimalPipe.transform(Math.abs(value), `1.${decimals}-${decimals}`);
  return value < 0 ? '(' + formattedValue + ')' : formattedValue;
};

export const _percentageRenderer = (value: any) => {
  const formattedValue = _decimalPipe.transform(Math.abs(value), '1.2-2');
  return (value < 0 ? '(' + formattedValue + ')' : formattedValue) + '%';
};

export const _amountRenderer = (value, side?: Side) => {
  if (value) {
    const formattedValue = _decimalPipe.transform(Math.abs(value), '1.2-6');
    return value < 0 || (side && side === Side.SELL) ? '(' + formattedValue + ')' : formattedValue;
  } else {
    return 0;
  }
};

export const _stringAmountRenderer = (value, side?: Side) => {
  if (value) {
    const formattedValue = _decimalPipe.transform(Math.abs(value), '1.2-2');
    return value < 0 || (side && side === Side.SELL) ? '(' + formattedValue + ')' : formattedValue;
  } else {
    return 0;
  }
}

export const _truncateToDecimalPlace = (value: number, numDecimalPlaces: number = 2): number => {
  const factor = Math.pow(10, numDecimalPlaces);
  return Math.trunc(value * factor) / factor;
};

export const scrollToTop = (element: any) => {
  const duration = 200;
  const interval = 5;
  const move = element.scrollTop * interval / duration;
  observableInterval(interval).pipe(
    scan((acc, curr) => acc - move, element.scrollTop),
    tap(position => element.scrollTop = position),
    takeWhile(val => val > 0)).subscribe();
}

export function multipleEmailValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    // Return null if empty (let Validators.required handle it if needed)
    if (!value) return null;

    // Split by comma and trim whitespace from each email
    const emails = value.split(',').map((email: string) => email.trim());
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,15}$/;
    
    // Find any email that doesn't match the pattern
    const invalidEmails = emails.filter((email: string) => !email || email === '' || (email && !emailRegex.test(email)));

    // Return an error object if any emails are invalid, otherwise return null
    return invalidEmails.length > 0  ? { 'invalidEmails': { emails: invalidEmails } } : null;
  };
}

export function getTimeDifference(targetTimestamp: number): string {
  // 1. Get the absolute difference in milliseconds
  const now = Date.now();
  const differenceMs = Math.abs(now - targetTimestamp);

  // 2. Define time constants in milliseconds
  const msPerMinute = 60 * 1000;
  const msPerHour = 60 * msPerMinute;
  const msPerDay = 24 * msPerHour;

  // 3. Calculate time components
  const days = Math.floor(differenceMs / msPerDay);
  const hours = Math.floor((differenceMs % msPerDay) / msPerHour);
  const minutes = Math.floor((differenceMs % msPerHour) / msPerMinute);

  // 4. Return the formatted string
  return `${days} days, ${hours} hours, ${minutes} minutes`;
}